/**
 * 管理端接口封装。
 *
 * ⚠️ token 用**独立**的 localStorage key（admin_token），不能复用 C 端的 'token'：
 *    两个体系共用 Authorization 请求头，靠 Sa-Token 的 loginType 区分（后端已隔离），
 *    但同浏览器同时开 C 端和后台时，共用一个 key 会互相覆盖登录态。
 */
const API_BASE = process.env.VUE_APP_API_BASE || 'http://localhost:8003'
const TOKEN_KEY = 'admin_token'
const BASE = '/api/admin/v1'

export function getAdminToken() {
  return localStorage.getItem(TOKEN_KEY) || ''
}

export function setAdminToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token)
  else localStorage.removeItem(TOKEN_KEY)
}

export function clearAdminToken() {
  localStorage.removeItem(TOKEN_KEY)
}

function authHeaders() {
  const token = getAdminToken()
  return token ? { 'Authorization': 'Bearer ' + token } : {}
}

function request(method, path, body) {
  return fetch(`${API_BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    credentials: 'include',
    body: body === undefined ? undefined : JSON.stringify(body)
  }).then(res => res.json())
}

function get(path) {
  return request('GET', path)
}

function post(path, body) {
  return request('POST', path, body)
}

function put(path, body) {
  return request('PUT', path, body)
}

function del(path) {
  return request('DELETE', path)
}

/** 把对象拼成查询串，跳过空值 */
function qs(params) {
  const parts = Object.keys(params || {})
    .filter(k => params[k] !== undefined && params[k] !== null && params[k] !== '')
    .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
  return parts.length ? '?' + parts.join('&') : ''
}

/**
 * 统一的成功判定：后端 Result 结构 { code, message, data }。
 * 与 C 端不同，管理端一律用**严格口径**（只认 code === 200）。
 */
export function isOk(res) {
  return !!res && res.code === 200
}

/** 未登录 / 会话过期：后端返回 9501 */
export function isNotLogin(res) {
  return !!res && res.code === 9501
}

export function adminLogin(username, password) {
  return post(`${BASE}/auth/login`, { username, password })
}

export function adminLogout() {
  return post(`${BASE}/auth/logout`)
}

export function adminMe() {
  return get(`${BASE}/auth/me`)
}

export function adminChangePassword(oldPassword, newPassword) {
  return post(`${BASE}/auth/change-password`, { oldPassword, newPassword })
}

// ---------------------------- 概览 ----------------------------

/** 概览统计：用户 / 内容 / 审核 / 邀请码 / 管理员 + 最近动态 */
export function fetchOverview() {
  return get(`${BASE}/overview`)
}

// ---------------------------- 用户管理 ----------------------------

export function fetchUsers({ keyword, status, page, size } = {}) {
  return get(`${BASE}/users${qs({ keyword, status, page, size })}`)
}

export function fetchUserDetail(id) {
  return get(`${BASE}/users/${id}`)
}

/**
 * status 取值：'normal' 启用 / 'disabled' 禁用
 * ⚠️ 必须传小写 —— users.status 实际存的就是小写（DDL 注释里的大写是错的）
 */
export function changeUserStatus(id, status) {
  return put(`${BASE}/users/${id}/status`, { status })
}

/**
 * 修改用户资料。
 * ⚠️ phone / email 传 null 或空串 = 不修改（不支持清空）。
 * 邮箱或手机号被其他账号占用时返回 9518。
 */
export function updateUser(id, { nickname, phone, email } = {}) {
  return put(`${BASE}/users/${id}`, { nickname, phone, email })
}

// ---------------------------- 内容审核 ----------------------------

/** type 必填：THOUGHT / SHARED_COMMENT / WORKSPACE_SHARE / PROJECT_SHARE */
export function fetchAuditContents({ type, keyword, userId, page, size } = {}) {
  return get(`${BASE}/audit/contents${qs({ type, keyword, userId, page, size })}`)
}

export function hideContent({ resourceType, resourceId, reason }) {
  return post(`${BASE}/audit/hide`, { resourceType, resourceId, reason })
}

export function restoreContent({ resourceType, resourceId, reason }) {
  return post(`${BASE}/audit/restore`, { resourceType, resourceId, reason })
}

/**
 * 修改内容正文。
 * ⚠️ 只有 THOUGHT（≤255 字）/ SHARED_COMMENT（≤100 字）有正文可改；
 *    分享类没有正文，后端返回 9506。
 */
export function updateContent({ resourceType, resourceId, content }) {
  return put(`${BASE}/audit/content`, { resourceType, resourceId, content })
}

// ---------------------------- 邀请码 ----------------------------

export function fetchInviteCodes({ code, status, batchNo, page, size } = {}) {
  return get(`${BASE}/invite-codes${qs({ code, status, batchNo, page, size })}`)
}

export function generateInviteCodes({ count, maxUseCount, validDays, remark } = {}) {
  return post(`${BASE}/invite-codes/generate`, { count, maxUseCount, validDays, remark })
}

/** status 只能传 'UNUSED'（启用）或 'DISABLED'（禁用） */
export function changeInviteCodeStatus(id, status) {
  return put(`${BASE}/invite-codes/${id}/status`, { status })
}

export function deleteInviteCode(id) {
  return del(`${BASE}/invite-codes/${id}`)
}

/**
 * 批量启用 / 禁用。status 只能 'UNUSED'（启用）或 'DISABLED'（禁用）。
 * 返回 { success, skipped } —— 状态不允许的单条会被跳过，不会整批失败。
 */
export function batchInviteCodeStatus(ids, status) {
  return post(`${BASE}/invite-codes/batch/status`, { ids, status })
}

/** 批量删除（软删除）。返回 { success, skipped } */
export function batchDeleteInviteCodes(ids) {
  return post(`${BASE}/invite-codes/batch/delete`, { ids })
}

export function fetchInviteCodeUsages(id, { page, size } = {}) {
  return get(`${BASE}/invite-codes/${id}/usages${qs({ page, size })}`)
}

// ---------------------------- 工作区浏览 ----------------------------

/**
 * 工作区列表（四级下钻第 1 层）。
 * keyword 匹配名称 / ID；userId 按归属用户过滤（用户详情页跳转用）；
 * type: PERSONAL / TEAM / ENTERPRISE；status: ACTIVE / ARCHIVED / DELETED；
 * includeDeleted 传 true 才含已删除（后端默认 false）。
 */
export function fetchWorkspaces({ keyword, userId, type, status, includeDeleted, page, size } = {}) {
  return get(`${BASE}/workspaces${qs({ keyword, userId, type, status, includeDeleted, page, size })}`)
}

/** 工作区详情（下钻到工作表列表时的工作区摘要 + 统计 + 分享可见性） */
export function fetchWorkspaceDetail(id) {
  return get(`${BASE}/workspaces/${id}`)
}

/** 工作区下的工作表列表（第 2 层） */
export function fetchWorkspaceProjects(id, { keyword, includeDeleted, page, size } = {}) {
  return get(`${BASE}/workspaces/${id}/projects${qs({ keyword, includeDeleted, page, size })}`)
}

/** 工作表详情（下钻到时间块列表时的工作表摘要 + 统计 + 分享可见性） */
export function fetchProjectDetail(id) {
  return get(`${BASE}/projects/${id}`)
}

/**
 * 工作表下的时间块列表（第 3 层）。
 * dateFrom / dateTo 形如 '2026-10-01'（el-date-picker 的默认格式），按计划开始时间过滤。
 */
export function fetchProjectTimeBlocks(id, { keyword, executionStatus, dateFrom, dateTo, includeDeleted, page, size } = {}) {
  return get(`${BASE}/projects/${id}/time-blocks${qs({ keyword, executionStatus, dateFrom, dateTo, includeDeleted, page, size })}`)
}

/**
 * 评论列表（第 4 层）。
 * resourceType + resourceId 成对传入查某资源的评论；只传 userId 查某人的全部评论。
 */
export function fetchComments({ resourceType, resourceId, userId, keyword, includeDeleted, page, size } = {}) {
  return get(`${BASE}/comments${qs({ resourceType, resourceId, userId, keyword, includeDeleted, page, size })}`)
}

// ---------------------------- 系统设置 ----------------------------

/** 系统设置列表：按分组返回，含 is_public=0 的非公开项 */
export function fetchSettings() {
  return get(`${BASE}/settings`)
}

/**
 * 修改设置值。
 * ⚠️ value 一律传**字符串**：类型校验在后端按该键声明的 valueType 做，
 *    前端不参与判断，避免两边规则不一致。
 * key 形如 auth.login.enabled，encodeURIComponent 不会编码点号。
 */
export function updateSetting(key, value) {
  return put(`${BASE}/settings/${encodeURIComponent(key)}`, { value })
}

/** 恢复为代码中声明的默认值（不是删除该行） */
export function resetSetting(key) {
  return post(`${BASE}/settings/${encodeURIComponent(key)}/reset`)
}

/**
 * 查询某个设置项的变更历史（按 system_settings.id 关联的操作日志）。
 * 返回最近 20 条 SETTING_UPDATE / SETTING_RESET 记录。
 */
export function fetchSettingHistory(key) {
  return get(`${BASE}/settings/${encodeURIComponent(key)}/history`)
}

/**
 * 当前未过期的验证码（排障用）。
 *
 * 后端直接扫 Redis（SCAN verify:*），不落库 —— 验证码是分钟级临时数据。
 * ⚠️ 列表只返回**脱敏**手机号/邮箱与掩码验证码（仅保留位数），不含明文。
 *    要看明文请用 revealVerifyCode，那条路径要求提供完整标识并单独记日志。
 *
 * @param {object} params
 * @param {string} [params.keyword] 手机号/邮箱关键字（按原始值匹配，展示值是脱敏的）
 * @param {string} [params.scene]   场景码过滤，如 RESET_PASSWORD
 */
export function fetchVerifyCodes({ keyword, scene } = {}) {
  return get(`${BASE}/settings/verify-codes${qs({ keyword, scene })}`)
}

/**
 * 查看某一条验证码的明文。
 *
 * ⚠️ 全项目唯一下发明文验证码的接口，每次调用写一条 VERIFY_CODE_REVEAL 操作日志。
 * 必须传**完整**手机号/邮箱（列表里是脱敏值，不能直接拿来调这个接口）。
 *
 * @param {object} payload
 * @param {string} payload.scene        场景码
 * @param {string} payload.identityType PHONE / EMAIL
 * @param {string} payload.identity     完整手机号或邮箱
 */
export function revealVerifyCode({ scene, identityType, identity }) {
  return post(`${BASE}/settings/verify-codes/reveal`, { scene, identityType, identity })
}

// ---------------------------- 飞书告警机器人 ----------------------------

/**
 * 告警链路状态。
 *
 * 返回 { webhookConfigured, secretConfigured, enabled, ready, maxPerMinute, dedupeMinutes,
 *        webhookEnvName, secretEnvName }。
 * ⚠️ 后端**不返回** webhook 地址和签名密钥本身，只有「已配置 / 未配置」——
 *    它们是密钥，只走环境变量（见后端 FeishuProperties 注释）。
 */
export function fetchAlertStatus() {
  return get(`${BASE}/alerts/feishu/status`)
}

/**
 * 发送测试消息。
 *
 * 跳过告警开关与节流限制，同步返回结果；失败时后端返回 9519 并带上飞书原始原因
 * （如 code=19021 签名不匹配）。每次调用都会写一条 ALERT_TEST 操作日志。
 */
export function sendAlertTest() {
  return post(`${BASE}/alerts/feishu/test`)
}

/** 最近告警发送记录（最新在前）。limit 默认 20，后端上限 50 */
export function fetchAlertLogs(limit) {
  return get(`${BASE}/alerts/feishu/logs${qs({ limit })}`)
}

// ---------------------------- 成就配置 ----------------------------

/** 成就定义列表。keyword 匹配编码/名称；status 传 ENABLED / DISABLED */
export function fetchAchievements({ keyword, status, categoryCode, page, size } = {}) {
  return get(`${BASE}/achievements${qs({ keyword, status, categoryCode, page, size })}`)
}

export function fetchAchievementDetail(id) {
  return get(`${BASE}/achievements/${id}`)
}

/**
 * 成就配置可选项（分类 / 等级 / 指标 / 图标 / 状态）。
 * 条件类型、时间窗口、进度单位由所选**指标**带出，前端不自己维护映射。
 */
export function fetchAchievementMeta() {
  return get(`${BASE}/achievements/meta`)
}

/**
 * 新增成就。
 * ⚠️ 默认保存为下架态草稿，传 enabled=true 才立即上架。
 * ⚠️ 条件类型 / 时间窗口 / 单位**不要传** —— 由 metricKey 唯一决定。
 */
export function createAchievement(payload) {
  return post(`${BASE}/achievements`, payload)
}

/** 编辑成就。code 不可修改，传了不同的值会被后端拒绝（9516） */
export function updateAchievement(id, payload) {
  return put(`${BASE}/achievements/${id}`, payload)
}

/** 上架 / 下架。下架不影响已解锁用户的历史记录 */
export function changeAchievementStatus(id, enabled) {
  return put(`${BASE}/achievements/${id}/status`, { enabled })
}

/** 删除成就。仅从未被解锁过时可删，否则返回 9514（只能下架） */
export function deleteAchievement(id) {
  return del(`${BASE}/achievements/${id}`)
}

/** 解锁试算（只读，不会写入解锁记录） */
export function previewAchievement(id, userId) {
  return get(`${BASE}/achievements/${id}/preview${qs({ userId })}`)
}

// ---------------------------- APP 云控 ----------------------------
//
// 全部接口都按「平台 + 环境」隔离（platform: ANDROID/IOS/HARMONY，env: PROD/PRE/TEST）。
// ⚠️ 后端只校验、不下发：真正决定客户端行为的是 /api/v1/app-config/bundle。
// ⚠️ 所有写操作都会落一条 admin_operation_logs（APP_VERSION_* / APP_FLAG_* / APP_POLICY_UPDATE）。

/** 版本列表。status 可选 DRAFT / PRE / PUBLISHED / OFFLINE，空表示全部 */
export function fetchAppVersions({ platform, env, status } = {}) {
  return get(`${BASE}/app-control/versions${qs({ platform, env, status })}`)
}

/** 新增版本：后端一律落为草稿，发布要走 changeAppVersionStatus */
export function createAppVersion(payload) {
  return post(`${BASE}/app-control/versions`, payload)
}

/** 编辑版本：平台 / 环境 / 版本号不可修改（后端忽略这三个字段） */
export function updateAppVersion(id, payload) {
  return put(`${BASE}/app-control/versions/${id}`, payload)
}

/**
 * 版本状态流转。
 * 允许方向：DRAFT→PRE/PUBLISHED/OFFLINE，PRE→PUBLISHED/OFFLINE，
 * PUBLISHED→OFFLINE，OFFLINE→PUBLISHED。非法方向返回 9523。
 */
export function changeAppVersionStatus(id, status) {
  return post(`${BASE}/app-control/versions/${id}/status`, { status })
}

/** 删除版本：仅草稿 / 已下线可删 */
export function deleteAppVersion(id) {
  return del(`${BASE}/app-control/versions/${id}`)
}

/** 功能开关列表 */
export function fetchAppFlags({ platform, env } = {}) {
  return get(`${BASE}/app-control/flags${qs({ platform, env })}`)
}

export function createAppFlag(payload) {
  return post(`${BASE}/app-control/flags`, payload)
}

/** 编辑开关：开关键不可修改 */
export function updateAppFlag(id, payload) {
  return put(`${BASE}/app-control/flags/${id}`, payload)
}

/** 切换总闸：关闭后对所有客户端为 false，白名单也不破例 */
export function setAppFlagEnabled(id, enabled) {
  return put(`${BASE}/app-control/flags/${id}/enabled`, { enabled })
}

export function deleteAppFlag(id) {
  return del(`${BASE}/app-control/flags/${id}`)
}

/**
 * 平台策略（版本检测总开关 / 强制下线 / 热修 / 封禁版本 / 调试白名单）。
 * 没保存过时返回代码默认值，且 persisted=false。
 */
export function fetchAppPolicy({ platform, env } = {}) {
  return get(`${BASE}/app-control/policy${qs({ platform, env })}`)
}

export function saveAppPolicy(payload) {
  return put(`${BASE}/app-control/policy`, payload)
}

// ---------------------------- 操作日志 ----------------------------

export function fetchOperationLogs({ adminId, action, page, size } = {}) {
  return get(`${BASE}/logs${qs({ adminId, action, page, size })}`)
}
